import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, Check } from "lucide-react";

// Single-file SPA landing page for promoting Tally customization & Tally <-> API integration
// Tailwind CSS utility classes are used for styling (no import needed here).
// Usage: place this file in a React app (e.g. /src/components/TallyLanding.jsx)
// Make sure Tailwind, framer-motion, and lucide-react are installed in your project.

export default function Maadhi() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState({ loading: false, success: null, error: null });

  const whatsappNumber = "919597171207"; // replace with your WhatsApp number in international format (no +)
  const whatsappMsg = encodeURIComponent("Hi! I'm interested in Tally customization and API integration. Please contact me.");

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus({ loading: true, success: null, error: null });

    // simple validation
    if (!form.name || (!form.email && !form.phone)) {
      setStatus({ loading: false, success: null, error: "Please provide your name and either email or phone." });
      return;
    }

    // POST to a serverless endpoint or API you control. Replace '/api/contact' with your real endpoint.
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error(`Server responded ${res.status}`);
      setStatus({ loading: false, success: "Thanks! We'll get back to you shortly.", error: null });
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus({ loading: false, success: null, error: "Could not send message. Please email us or message on WhatsApp." });
      console.error(err);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900">
      <header className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-emerald-400 flex items-center justify-center text-white font-bold">T</div>
          <div>
            <h1 className="text-xl font-semibold">Tally Customization & API Integration</h1>
            <p className="text-sm text-slate-600">Reliable Tally tailoring, connectors & automation for your business</p>
          </div>
        </div>
        <nav className="hidden md:flex gap-6 items-center">
          <a href="#services" className="text-slate-700 hover:text-slate-900">Services</a>
          <a href="#pricing" className="text-slate-700 hover:text-slate-900">Pricing</a>
          <a href="#contact" className="text-indigo-600 font-medium">Contact</a>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-6">
        <section className="grid md:grid-cols-2 gap-10 items-center py-12">
          <motion.div initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5 }}>
            <h2 className="text-3xl md:text-4xl font-extrabold leading-tight">Make Tally work the way your business does</h2>
            <p className="mt-4 text-slate-600">Custom reports, automated voucher flows, inventory controls, and secure bi-directional API integrations to sync Tally with your web apps, CRMs or third-party systems.</p>

            <ul className="mt-6 grid gap-3">
              <li className="flex items-start gap-3">
                <span className="p-2 bg-emerald-50 rounded-full text-emerald-600"><Check size={16} /></span>
                <div>
                  <div className="font-medium">Tally customization</div>
                  <div className="text-sm text-slate-600">TDL, reports, masters, and custom voucher behaviours.</div>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <span className="p-2 bg-indigo-50 rounded-full text-indigo-600"><MessageCircle size={16} /></span>
                <div>
                  <div className="font-medium">Tally ↔ API integration</div>
                  <div className="text-sm text-slate-600">Push/pull invoices, stock, and ledgers via secure APIs and middleware.</div>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <span className="p-2 bg-yellow-50 rounded-full text-yellow-600"><Phone size={16} /></span>
                <div>
                  <div className="font-medium">Support & automation</div>
                  <div className="text-sm text-slate-600">Scheduled syncs, monitoring, and rapid bugfixes.</div>
                </div>
              </li>
            </ul>

            <div className="mt-8 flex gap-3">
              <a href={`https://wa.me/${whatsappNumber}?text=${whatsappMsg}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg shadow hover:brightness-95">Chat on WhatsApp</a>
              <a href="#contact" className="inline-flex items-center gap-2 px-4 py-2 border rounded-lg">Request callback</a>
            </div>
          </motion.div>

          <motion.div initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5 }} className="bg-white rounded-2xl p-6 shadow-md">
            <div className="text-sm text-slate-500">Quick demo</div>
            <div className="mt-4 grid gap-3">
              <div className="p-4 border rounded-md">
                <div className="text-xs text-slate-500">Use case</div>
                <div className="font-medium">Auto-push Sales from Webstore to Tally</div>
                <div className="text-sm text-slate-600 mt-2">Orders from your e-commerce store are validated and sent to Tally as sales vouchers instantly or in scheduled batches.</div>
              </div>

              <div className="p-4 border rounded-md">
                <div className="text-xs text-slate-500">Integration</div>
                <div className="font-medium">Webhook → Middleware → Tally (XML/TCP)</div>
                <div className="text-sm text-slate-600 mt-2">We build middlewares that translate your JSON/API to Tally-compatible XML or use Tally's ODBC/ODATA if preferable.</div>
              </div>
            </div>
          </motion.div>
        </section>

        <section id="services" className="py-8">
          <h3 className="text-2xl font-semibold">Services</h3>
          <div className="mt-6 grid md:grid-cols-3 gap-6">
            {[
              { title: "Custom TDL & Reports", desc: "Create tailored ledgers, voucher flows and reports to match your accounting policies." },
              { title: "API & Middleware", desc: "Secure push/pull integrations, token auth, and retry logic for robust syncs." },
              { title: "Migration & Support", desc: "Data migrations, audit-ready imports and ongoing operational support." },
            ].map((s) => (
              <div key={s.title} className="p-6 bg-white rounded-2xl shadow-sm">
                <div className="font-semibold">{s.title}</div>
                <div className="text-sm text-slate-600 mt-2">{s.desc}</div>
                <div className="mt-4 text-sm text-slate-500">From ₹8,000 + GST (estimate)</div>
              </div>
            ))}
          </div>
        </section>

        <section id="pricing" className="py-8">
          <h3 className="text-2xl font-semibold">Packages</h3>
          <div className="mt-6 grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-gradient-to-b from-white to-slate-50 rounded-2xl border">
              <div className="text-sm text-slate-500">Starter</div>
              <div className="mt-2 text-2xl font-bold">₹8,000</div>
              <div className="text-sm text-slate-600 mt-2">One-off report or simple TDL change</div>
            </div>
            <div className="p-6 bg-white rounded-2xl border shadow-sm">
              <div className="text-sm text-slate-500">Integration</div>
              <div className="mt-2 text-2xl font-bold">₹25,000+</div>
              <div className="text-sm text-slate-600 mt-2">API connector, webhook delivery & middleware</div>
            </div>
            <div className="p-6 bg-white rounded-2xl border">
              <div className="text-sm text-slate-500">Support</div>
              <div className="mt-2 text-2xl font-bold">Monthly ₹5,000+</div>
              <div className="text-sm text-slate-600 mt-2">Monitoring, fixes & scheduled syncs</div>
            </div>
          </div>
        </section>

        <section id="contact" className="py-12">
          <h3 className="text-2xl font-semibold">Contact us</h3>

          <div className="mt-6 grid md:grid-cols-2 gap-8 items-start">
            <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-2xl shadow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input name="name" value={form.name} onChange={handleChange} placeholder="Your name*" className="p-3 border rounded" />
                <input name="company" value={form.company} onChange={handleChange} placeholder="Company (optional)" className="p-3 border rounded" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input name="email" value={form.email} onChange={handleChange} placeholder="Email" className="p-3 border rounded" />
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone" className="p-3 border rounded" />
              </div>

              <textarea name="message" value={form.message} onChange={handleChange} placeholder="Brief message (what do you want automated?)" className="p-3 border rounded h-28 w-full" />

              <div className="flex gap-3 items-center">
                <button type="submit" disabled={status.loading} className="px-4 py-2 bg-indigo-600 text-white rounded-lg">{status.loading ? "Sending..." : "Send message"}</button>
                <a href={`mailto:enquiry@maadhi.com?subject=${encodeURIComponent("Tally customization enquiry")}`} className="text-sm text-slate-600">Or email us</a>
              </div>

              {status.error && <div className="text-sm text-red-600">{status.error}</div>}
              {status.success && <div className="text-sm text-emerald-600">{status.success}</div>}
            </form>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-2xl shadow">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-indigo-50 rounded"><Mail /></div>
                  <div>
                    <div className="font-medium">enquiry@maadhi.com</div>
                    <div className="text-sm text-slate-500">We reply within 24 business hours</div>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <div className="p-3 bg-emerald-50 rounded"><Phone /></div>
                  <div>
                    <div className="font-medium">+91 9597171207</div>
                    <div className="text-sm text-slate-500">Call or WhatsApp</div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="text-sm text-slate-500">Or chat instantly</div>
                  <a href={`https://wa.me/${whatsappNumber}?text=${whatsappMsg}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded">Open WhatsApp</a>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow text-sm text-slate-600">
                <div className="font-medium">What we need from you</div>
                <ul className="list-disc ml-5 mt-2">
                  <li>Sample CSV or API docs</li>
                  <li>Access details for a test environment</li>
                  <li>Desired voucher/ledger mapping</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <footer className="py-8 text-center text-sm text-slate-500">© {new Date().getFullYear()} Maadhi — Tally customization & integrations</footer>
      </main>

      {/* Floating WhatsApp */}
      <a href={`https://wa.me/${whatsappNumber}?text=${whatsappMsg}`} target="_blank" rel="noreferrer" className="fixed right-6 bottom-6 bg-emerald-600 p-4 rounded-full shadow-lg flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-4 4V5a4 4 0 0 1 4-4h9a4 4 0 0 1 4 4z"/></svg>
      </a>
    </div>
  );
}
